import React, { Fragment, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Box, Button, Grid, IconButton, InputLabel, Stack, TextField, Typography } from '@mui/material';
import CustomTooltip from 'components/CustomTooltip';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormikProps, getIn } from 'formik';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { PatternFormat } from 'react-number-format';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import statesTitlecase from 'utils/states-titlecase';
import { CompanyType, useDeleteOwnerMutation } from 'graphql/types';

dayjs.extend(utc);
const maxDateOfBirth = dayjs().subtract(18, 'year').toDate();

export type CompanyOwnerFormValues = {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string;
  bizPhone: string;
  cellPhone: string;
  bizEmail: string;
  dateOfBirth: string;
  ssn: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
};

export type CompanyInfoFormValues = {
  legalName: string;
  dba: string;
  email: string[];
  www: string;
  phone: string[];
  taxId: string;
  type: CompanyType[];
  linkToFiles: string;
  owners: CompanyOwnerFormValues[];
};

export interface CompanyOwnersTabProps {
  formik: FormikProps<CompanyInfoFormValues>;
}

export type CompanyOwnersTabHandle = {
  setActiveOwnerIndex: (index: number) => void;
};

const CompanyOwnersTab = forwardRef<CompanyOwnersTabHandle, CompanyOwnersTabProps>(function CompanyOwnersTab({ formik }, ref) {
  const { errors, touched, values, handleBlur, setFieldValue } = formik;
  const [activeIndex, setActiveIndex] = useState(0);
  const ownersLenRef = useRef(0);
  ownersLenRef.current = values.owners?.length || 0;

  useImperativeHandle(ref, () => ({
    setActiveOwnerIndex: (index: number) => {
      const len = ownersLenRef.current;
      if (len === 0) return;
      setActiveIndex(Math.max(0, Math.min(index, len - 1)));
    }
  }));
  const [deleteOwnerMutation] = useDeleteOwnerMutation();
  const updateOwner = (index: number, updates: Partial<CompanyOwnerFormValues>): void => {
    const updated = [...values.owners];
    updated[index] = { ...updated[index], ...updates };
    setFieldValue('owners', updated);
  };

  const addOwner = () => {
    const newOwners: CompanyOwnerFormValues[] = [...values.owners, {
      id: '',
      firstName: '', lastName: '', middleName: '',
      bizPhone: '', cellPhone: '', bizEmail: '',
      dateOfBirth: '', ssn: '',
      addressLine1: '', addressLine2: '',
      city: '', state: '', zip: ''
    }];
    setFieldValue('owners', newOwners);
    setActiveIndex(newOwners.length - 1);
  };

  const deleteOwner = async (index: number) => {
    const owner = values.owners[index];

    if (owner.id) {
      try {
        await deleteOwnerMutation({ variables: { id: owner.id } });
      } catch (e) {
        console.error('Failed to delete owner', e);
        return;
      }
    }

    const updated = [...values.owners];
    updated.splice(index, 1);
    setFieldValue('owners', updated);
    if (updated.length === 0) {
      setActiveIndex(0);
    } else {
      setActiveIndex(index >= updated.length ? Math.max(0, updated.length - 1) : index);
    }
  };
  const owners = values.owners || [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={0.75} sx={{ p: 0, minHeight: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap',
            pt: 0.5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              flexGrow: 1,
              alignItems: 'center'
            }}
          >
            {owners.length === 0 ? (
              <Typography variant="body1" sx={{ px: 1 }}>
                Click &apos;+&apos; to add the first Owner
              </Typography>
            ) : (
              owners.map((_, index: number) => (
                <Box
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    cursor: 'pointer',
                    padding: '10px',
                    borderBottom: activeIndex === index ? '2px solid #3f51b5' : 'none',
                    display: 'inline-block',
                    flexShrink: 0
                  }}
                >
                  Owner {index + 1}
                </Box>
              ))
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0 }}>
            <CustomTooltip title="Add owner" placement="top">
              <Button onClick={addOwner} variant="outlined" aria-label="Add owner" sx={{ ml: 1, minWidth: '30px', height: '30px' }}>
                <PlusOutlined />
              </Button>
            </CustomTooltip>
            {owners.length > 0 && (
              <CustomTooltip title="Delete owner" placement="top">
                <IconButton
                  onClick={() => deleteOwner(activeIndex)}
                  size="large"
                  color="error"
                  sx={{ ml: 1 }}
                  aria-label="Delete owner"
                >
                  <DeleteFilled />
                </IconButton>
              </CustomTooltip>
            )}
          </Box>
        </Box>

        {owners.map((owner, index: number) => (
          <Fragment key={owner.id || `owner-${index}`}>
            {activeIndex === index && (
              <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <Grid container columnSpacing={3} rowSpacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1.25}>
                      <InputLabel required>First Name</InputLabel>
                      <TextField variant="filled" fullWidth placeholder="First Name"
                        name={`owners[${index}].firstName`}
                        value={owner.firstName}
                        onChange={(e) => updateOwner(index, { firstName: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].firstName`) && getIn(errors, `owners[${index}].firstName`))}
                        helperText={getIn(touched, `owners[${index}].firstName`) && getIn(errors, `owners[${index}].firstName`)} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1.25}>
                      <InputLabel required>Last Name</InputLabel>
                      <TextField variant="filled" fullWidth placeholder="Last Name"
                        name={`owners[${index}].lastName`}
                        value={owner.lastName}
                        onChange={(e) => updateOwner(index, { lastName: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].lastName`) && getIn(errors, `owners[${index}].lastName`))}
                        helperText={getIn(touched, `owners[${index}].lastName`) && getIn(errors, `owners[${index}].lastName`)} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1.25}>
                      <InputLabel>Email</InputLabel>
                      <TextField variant="filled" fullWidth placeholder="Email"
                        name={`owners[${index}].bizEmail`}
                        value={owner.bizEmail}
                        onChange={(e) => updateOwner(index, { bizEmail: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].bizEmail`) && getIn(errors, `owners[${index}].bizEmail`))}
                        helperText={getIn(touched, `owners[${index}].bizEmail`) && getIn(errors, `owners[${index}].bizEmail`)} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1.25}>
                      <InputLabel>Phone</InputLabel>
                      <PatternFormat variant="filled" fullWidth customInput={TextField}
                        format="+1 (###) ###-####" mask="_" placeholder="Phone"
                        value={owner.bizPhone}
                        onChange={(e) => updateOwner(index, { bizPhone: e.target.value })} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1.25}>
                      <InputLabel>Date of Birth</InputLabel>
                      <DesktopDatePicker
                        value={owner.dateOfBirth ? dayjs.utc(owner.dateOfBirth).toDate() : null}
                        maxDate={maxDateOfBirth}
                        onChange={(value) => updateOwner(index, { dateOfBirth: value ? dayjs(value).format('YYYY-MM-DD') : '' })}
                        slotProps={{ textField: { variant: 'filled', fullWidth: true, size: 'medium', sx: { backgroundColor: 'transparent' }, name: `dateOfBirth-${index}`, onBlur: handleBlur } }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1.25}>
                      <InputLabel>SSN</InputLabel>
                      <PatternFormat variant="filled" fullWidth customInput={TextField}
                        format="###-##-####" mask="_" placeholder="SSN"
                        value={owner.ssn}
                        onChange={(e) => updateOwner(index, { ssn: e.target.value })}
                        error={Boolean(getIn(touched, `owners[${index}].ssn`) && getIn(errors, `owners[${index}].ssn`))}
                        helperText={getIn(touched, `owners[${index}].ssn`) && getIn(errors, `owners[${index}].ssn`)} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack spacing={1.25}>
                      <InputLabel required>Address</InputLabel>
                      <TextField variant="filled" fullWidth placeholder="Address"
                        name={`owners[${index}].addressLine1`}
                        value={owner.addressLine1 || ''}
                        onChange={(e) => updateOwner(index, { addressLine1: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].addressLine1`) && getIn(errors, `owners[${index}].addressLine1`))}
                        helperText={getIn(touched, `owners[${index}].addressLine1`) && getIn(errors, `owners[${index}].addressLine1`)} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    <Stack spacing={1.25}>
                      <InputLabel required>City</InputLabel>
                      <TextField variant="filled" fullWidth placeholder="City"
                        name={`owners[${index}].city`}
                        value={owner.city || ''}
                        onChange={(e) => updateOwner(index, { city: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].city`) && getIn(errors, `owners[${index}].city`))}
                        helperText={getIn(touched, `owners[${index}].city`) && getIn(errors, `owners[${index}].city`)} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Stack spacing={1.25}>
                      <InputLabel required>State</InputLabel>
                      <TextField variant="filled" select fullWidth
                        name={`owners[${index}].state`}
                        value={owner.state || ''}
                        onChange={(e) => updateOwner(index, { state: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].state`) && getIn(errors, `owners[${index}].state`))}
                        helperText={getIn(touched, `owners[${index}].state`) && getIn(errors, `owners[${index}].state`)}
                        SelectProps={{ native: true }}>
                        <option value="">State</option>
                        {statesTitlecase.map(({ name, abbreviation }) => (
                          <option key={abbreviation} value={name}>{abbreviation}</option>
                        ))}
                      </TextField>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Stack spacing={1.25}>
                      <InputLabel required>ZIP Code</InputLabel>
                      <TextField variant="filled" fullWidth placeholder="ZIP Code"
                        name={`owners[${index}].zip`}
                        value={owner.zip || ''}
                        onChange={(e) => updateOwner(index, { zip: e.target.value })}
                        onBlur={handleBlur}
                        error={Boolean(getIn(touched, `owners[${index}].zip`) && getIn(errors, `owners[${index}].zip`))}
                        helperText={getIn(touched, `owners[${index}].zip`) && getIn(errors, `owners[${index}].zip`)} />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Fragment>
        ))}
      </Stack>
    </LocalizationProvider>
  );
});

export default CompanyOwnersTab;
